import type { Concert } from './program-schema.ts'

/**
 * Concert timing, in Oslo time, independent of the host timezone.
 *
 * All four 2026 festival days fall inside CEST with no DST transition, so a fixed
 * `+02:00` offset resolves a `day` + `HH:MM` pair to the right instant without a
 * timezone library. "What day is it in Oslo right now?" is a different question —
 * that one uses `Intl`, because it must stay correct whenever it is asked.
 */

export type ConcertStatus = 'kommer' | 'paagaar' | 'ferdig'

/** CEST. Correct for 2026-08-12 … 2026-08-15; see KTD14 in the plan. */
const OSLO_SUMMER_OFFSET = '+02:00'

const osloDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Oslo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/** The `YYYY-MM-DD` calendar date in Oslo at the given instant. */
export function osloDateOf(now: Date): string {
  return osloDateFormatter.format(now)
}

function instant(day: string, time: string): Date {
  return new Date(`${day}T${time}:00${OSLO_SUMMER_OFFSET}`)
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Absolute start and end for a concert.
 *
 * An `end` earlier than `start` is read as crossing midnight. The main festival
 * programme has no such concerts — the latest finish is 23:00 — but the guard costs
 * nothing and stops a data edit from producing a negative-length concert.
 */
export function concertBounds(concert: Concert): { start: Date; end: Date } {
  const start = instant(concert.day, concert.start)
  let end = instant(concert.day, concert.end)
  if (end.getTime() < start.getTime()) {
    end = new Date(end.getTime() + DAY_MS)
  }
  return { start, end }
}

export function concertStatus(concert: Concert, now: Date): ConcertStatus {
  const { start, end } = concertBounds(concert)
  const t = now.getTime()
  // Every comparison against NaN is false, so a malformed time would otherwise
  // fall through to 'paagaar' and render as permanently live, at every hour of
  // every day. The snapshot is validated at fetch time, but it is loaded with an
  // unchecked cast, so fail to the neutral state rather than to a confident lie.
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 'kommer'
  if (t < start.getTime()) return 'kommer'
  if (t >= end.getTime()) return 'ferdig'
  return 'paagaar'
}

/**
 * The festival day currently being lived through, or null outside the festival.
 *
 * A plain calendar-date match is enough: no concert starts before 13:00 and none
 * runs past 23:00, so a festival day never outlives its own date and there is no
 * "festival night" cutoff to infer.
 */
export function currentFestivalDay(days: readonly string[], now: Date): string | null {
  const today = osloDateOf(now)
  return days.includes(today) ? today : null
}

/**
 * Where the "now" marker belongs in a day's list: the slot a concert starting right
 * now would occupy, i.e. before the first concert that has not started yet.
 *
 * The list is sorted by start time, so this is the only position that keeps it
 * reading forwards. Anchoring on end time instead puts the marker above concerts
 * that started earlier and are still playing, which renders as 15:20, "16:32 NOW",
 * 15:40 — a list that appears to go backwards. Concerts in progress therefore sit
 * just above the line, carrying their own live styling.
 *
 * Returns 0 before the day starts and the list length once everything has started.
 */
export function nowMarkerIndex(dayConcerts: readonly Concert[], now: Date): number {
  const t = now.getTime()
  const index = dayConcerts.findIndex((c) => {
    const start = concertBounds(c).start.getTime()
    // Keep this in step with concertStatus, which reports an unparseable concert
    // as 'kommer'. `NaN > t` is false, so without the guard the marker would be
    // placed after a concert the list is simultaneously rendering as upcoming.
    if (Number.isNaN(start)) return true
    return start > t
  })
  return index === -1 ? dayConcerts.length : index
}

export function formatTimeRange(concert: Concert): string {
  return `${concert.start}–${concert.end}`
}

/** Norwegian weekday + date, e.g. `onsdag 12. august`. */
const dayLabelFormatter = new Intl.DateTimeFormat('nb-NO', {
  timeZone: 'Europe/Oslo',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
})

export function formatDayLabel(day: string): string {
  const label = dayLabelFormatter.format(new Date(`${day}T12:00:00${OSLO_SUMMER_OFFSET}`))
  // Sentence case, not title case: Norwegian lowercases weekday and month names, so
  // only the first letter is raised. CSS `capitalize` would give "Onsdag 12. August".
  return label.charAt(0).toUpperCase() + label.slice(1)
}

/** Short Norwegian weekday for the day tabs, e.g. `ons`. */
const shortDayFormatter = new Intl.DateTimeFormat('nb-NO', {
  timeZone: 'Europe/Oslo',
  weekday: 'short',
})

export function formatDayShort(day: string): { weekday: string; dayOfMonth: string } {
  const date = new Date(`${day}T12:00:00${OSLO_SUMMER_OFFSET}`)
  return {
    weekday: shortDayFormatter.format(date).replace(/\.$/, ''),
    dayOfMonth: String(date.getUTCDate()),
  }
}
